import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router'
import { DJ_COURT_SYSTEM_ID, DJ_KEY } from '@ftr/contracts/digital-justice'
import { AppPaths, AuthenticationActions, SignupParams } from '@ftr/routing-paths'
import { lastValueFrom } from 'rxjs'
import { AuthenticationService, DigitalJusticeAuthService } from '../../services'

@Injectable({
  providedIn: 'root',
})
export class AuthenticationGuard {
  hasRedirectBeenChecked = false

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly digitalJusticeAuthService: DigitalJusticeAuthService,
  ) {}

  canActivate(activatedRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.authenticationService.isLoggedIn.then(async isLoggedIn => {
      if (!isLoggedIn) {
        const authenticationDefaultAction = activatedRoute.queryParamMap.get(SignupParams.AuthenticationDefaultAction)
        if (authenticationDefaultAction === AuthenticationActions.SignUp) {
          const authenticationEmail = activatedRoute.queryParamMap.get(SignupParams.AuthenticationEmail)
          this.router.navigate(['/signup'], {
            queryParams: { returnUrl: state.url, authenticationEmail },
          })
        } else {
          this.router.navigate([`/${AppPaths.Login}`], {
            queryParams: { returnUrl: state.url },
          })
        }
        return false
      } else {
        /**
         * Set the currentUser into the authenticationService if not present at this point.
         * This could happen when we refresh the page.
         */
        if (!this.authenticationService.getCurrentUserId()) {
          await this.authenticationService.initialize()
        }

        const courtSystemId = activatedRoute.queryParamMap.get(DJ_COURT_SYSTEM_ID)
        const djKey = activatedRoute.queryParamMap.get(DJ_KEY)
        if (courtSystemId && djKey) {
          return lastValueFrom(this.digitalJusticeAuthService.handlePostKey(courtSystemId, djKey)).then(() => true)
        }

        // Set the flag to true to prevent redirects after the first reload
        const shouldRedirectToCourtHome = await this.authenticationService.shouldRedirectToCourtHome()
        if (!this.hasRedirectBeenChecked && shouldRedirectToCourtHome && state.url === '/') {
          this.hasRedirectBeenChecked = true
          const targetUrl = this.authenticationService.getValidUserAwareReturnUrl(undefined)
          const previousUrl = this.router.currentNavigation()?.previousNavigation
          // Skip Redirect if the target url is the same as the previous url
          if (!!previousUrl && targetUrl === previousUrl.finalUrl?.toString()) {
            return true
          }
          this.router.navigate([targetUrl])
          return false
        }
        this.hasRedirectBeenChecked = true
        return true
      }
    })
  }
}
